import { useEffect, useState } from "react";
import { Gift, Copy, Check, Users, Clock, Star, Coins, Award, Download } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, StatCard, SectionCard } from "@/components/ui-helpers";
import { toast } from "sonner";

export default function RewardsPage() {
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/referral").then((r) => setD(r.data)).catch(() => setD({})); }, []);

  const link = d?.link ? `${window.location.origin}/register?ref=${d.code}` : "";
  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true); toast.success("Referral link copied"); setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      try {
        const ta = document.createElement("textarea");
        ta.value = link; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
        setCopied(true); toast.success("Referral link copied"); setTimeout(() => setCopied(false), 1500);
      } catch (e2) {
        toast.error("Couldn't copy — please copy the link manually.");
      }
    }
  };

  const s = d?.stats || {};
  const exportPoints = () => {
    const txns = d?.transactions || [];
    if (!txns.length) { toast.error("No points activity to export yet."); return; }
    const rows = [["Date", "Type", "Reason", "Points"], ...txns.map((t) => [String(t.created_at || "").slice(0, 10), t.type, t.reason || "", t.amount])];
    const csv = rows.map((r) => r.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const u = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = u; a.download = "points_history.csv"; a.click(); URL.revokeObjectURL(u);
  };
  return (
    <div>
      <PageHeader title="Offers & Rewards" subtitle="Refer friends, earn points and unlock rewards." icon={Gift} />
      <div className="card-soft mb-5 overflow-hidden">
        <div className="brand-gradient-bg p-6 text-white">
          <h2 className="font-brand text-2xl font-bold">Refer &amp; Earn</h2>
          <p className="mt-1 text-white/80">Share your unique link. When a friend joins and qualifies, you earn points.</p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <div className="flex-1 rounded-xl bg-white/15 px-4 py-3 font-mono text-sm" data-testid="referral-link">{link || "Loading..."}</div>
            <button onClick={copy} data-testid="copy-referral" className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 font-semibold text-blue-600">
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy Link"}
            </button>
          </div>
          <p className="mt-3 text-sm text-white/80">Your code: <span className="font-bold">{d?.code}</span>
            <span className="ml-3 inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-0.5 text-xs font-semibold" data-testid="hero-tier"><Award className="h-3.5 w-3.5" /> {s.tier || "Bronze"} tier</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={Users} label="Total Referrals" value={s.total ?? 0} tint="blue" />
        <StatCard icon={Clock} label="Pending" value={s.pending ?? 0} tint="amber" />
        <StatCard icon={Star} label="Successful" value={s.successful ?? 0} tint="emerald" />
        <StatCard icon={Coins} label="Points Balance" value={s.points ?? 0} tint="purple" />
      </div>

      <div className="card-soft mt-5 p-5" data-testid="referral-tier">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl brand-gradient-bg text-white"><Award className="h-5 w-5" /></span>
          <div><p className="text-xs text-slate-400">Your referrer tier</p><p className="text-lg font-bold text-slate-800" data-testid="tier-name">{s.tier || "Bronze"}{s.tier === "Ambassador" ? " 🏆" : ""}</p></div>
          <span className="ml-auto text-sm text-slate-500">Earning <b className="text-slate-700">{s.per_referral ?? 500}</b> pts per successful referral</span>
        </div>

        {s.next_at ? (
          <>
            <div className="mt-3 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full brand-gradient-bg transition-all" style={{ width: `${Math.min(100, ((s.qualified || 0) / s.next_at) * 100)}%` }} data-testid="tier-progress" /></div>
            <p className="mt-1 text-xs text-slate-400">{s.next_at - (s.qualified || 0)} more successful referral(s) to reach {s.next_at === 3 ? "Silver (750 pts each)" : "Ambassador (1,000 pts each)"}.</p>
          </>
        ) : <p className="mt-2 text-xs font-medium text-emerald-600">You've reached the top Ambassador tier — maximum rewards unlocked!</p>}
      </div>

      <SectionCard title="Points Ledger" className="mt-5"
        action={d?.transactions?.length ? <button onClick={exportPoints} data-testid="points-export" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-medium text-slate-600 hover:border-emerald-300 hover:text-emerald-600"><Download className="h-3.5 w-3.5" /> Export CSV</button> : null}>
        {d?.transactions?.length ? (
          <div className="divide-y divide-slate-50">
            {d.transactions.map((t, i) => (
              <div key={i} className="flex items-center justify-between py-2 text-sm">
                <span className="min-w-0 flex-1 truncate text-slate-600">{t.reason || t.type}<span className="ml-2 text-xs text-slate-400">{String(t.created_at || "").slice(0, 10)}</span></span>
                <span className={`ml-3 shrink-0 font-semibold ${t.amount >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
              </div>
            ))}
          </div>
        ) : <p className="py-4 text-sm text-slate-400">Your points activity will appear here as you refer friends. Points use a verified ledger — no fake balances.</p>}
      </SectionCard>
    </div>
  );
}
